import { createFileRoute, redirect } from "@tanstack/react-router"
import { useState } from "react"
import {
  Container,
  Title,
  Grid,
  Card,
  Text,
  Group,
  Badge,
  Stack,
  Alert,
  Button,
  PasswordInput,
  Progress,
  Table,
  useMantineColorScheme,
} from "@mantine/core"
import { IconAlertCircle, IconTrendingUp, IconUsers, IconEye, IconLock, IconCheck, IconX } from "@tabler/icons-react"
import { MetricsService, type RealMetrics, type PublicMetrics } from "../services/metrics.service"

function MetricsDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authKey, setAuthKey] = useState("")
  const [authError, setAuthError] = useState("")
  const [realMetrics, setRealMetrics] = useState<RealMetrics | null>(null)
  const [staticMetrics, setStaticMetrics] = useState<PublicMetrics | null>(null)
  const [loading, setLoading] = useState(false)
  const { colorScheme } = useMantineColorScheme()

  const handleAuth = async () => {
    if (authKey !== "rtefn-internal-2025") {
      setAuthError("Invalid authentication key")
      return
    }

    setLoading(true)
    setAuthError("")

    try {
      const metrics = await MetricsService.getRealMetrics(authKey)
      const publicMetrics = await MetricsService.getPublicRealMetrics()
      if (metrics) {
        setRealMetrics(metrics)
        setStaticMetrics(publicMetrics)
        setIsAuthenticated(true)
      } else {
        setAuthError("Failed to authenticate or fetch metrics")
      }
    } catch {
      setAuthError("Error accessing metrics dashboard")
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleAuth()
    }
  }

  if (!isAuthenticated) {
    return (
      <Container size="sm" py="xl">
        <Stack gap="xl" ta="center">
          <IconLock size={64} className={colorScheme === "dark" ? "text-gray-400" : "text-gray-500"} />

          <Title order={2} className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
            RTEFN Metrics Dashboard
          </Title>

          <Text className={colorScheme === "dark" ? "text-white/70" : "text-gray-600"}>
            This dashboard contains internal metrics and analytics. Access is restricted to authorized personnel only.
          </Text>

          <Card
            shadow="md"
            radius="lg"
            p="xl"
            style={{
              background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
              border: colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
            }}
          >
            <Stack gap="md">
              <PasswordInput
                label="Authentication Key"
                placeholder="Enter access key..."
                value={authKey}
                onChange={(event) => setAuthKey(event.currentTarget.value)}
                onKeyPress={handleKeyPress}
                error={authError}
                size="lg"
              />

              <Button
                onClick={handleAuth}
                loading={loading}
                size="lg"
                fullWidth
                style={{
                  background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                }}
              >
                Access Dashboard
              </Button>
            </Stack>
          </Card>

          <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
            <Text size="sm">
              This is an internal tool for tracking real engagement metrics. Contact the development team if you need
              access.
            </Text>
          </Alert>
        </Stack>
      </Container>
    )
  }

  if (!realMetrics) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" icon={<IconX size={16} />}>
          Failed to load metrics data. Please try again.
        </Alert>
      </Container>
    )
  }

  const comparisonData = staticMetrics
    ? [
        {
          metric: "Total Actions",
          static: staticMetrics.totalActions,
          real: realMetrics.totalActions,
          type: "count",
        },
        {
          metric: "Phone Calls",
          static: staticMetrics.actionsByType["phone"] || 0,
          real: realMetrics.actionsByType["phone"] || 0,
          type: "count",
        },
        {
          metric: "Emails",
          static: staticMetrics.actionsByType["email"] || 0,
          real: realMetrics.actionsByType["email"] || 0,
          type: "count",
        },
        {
          metric: "Letters",
          static: staticMetrics.actionsByType["letter"] || 0,
          real: realMetrics.actionsByType["letter"] || 0,
          type: "count",
        },
        {
          metric: "Social Media",
          static: staticMetrics.actionsByType["social"] || 0,
          real: realMetrics.actionsByType["social"] || 0,
          type: "count",
        },
      ]
    : []

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={1} className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
              Internal Metrics Dashboard
            </Title>
            <Text className={colorScheme === "dark" ? "text-white/70" : "text-gray-600"}>
              Real vs. displayed metrics for the RTEFN Take Action campaign
            </Text>
          </div>

          <Badge variant="light" color="green" size="lg" leftSection={<IconCheck size={16} />}>
            Authenticated
          </Badge>
        </Group>

        {/* Overview Cards */}
        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card
              shadow="sm"
              radius="lg"
              p="lg"
              style={{
                background: colorScheme === "dark" ? "rgba(16, 185, 129, 0.1)" : "rgba(16, 185, 129, 0.05)",
                border: "1px solid rgba(16, 185, 129, 0.2)",
              }}
            >
              <Group gap="sm" mb="xs">
                <IconUsers size={20} className="text-green-500" />
                <Text fw={600} size="sm" className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
                  Real Actions
                </Text>
              </Group>
              <Text size="2rem" fw={700} className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
                {MetricsService.formatNumber(realMetrics.totalActions)}
              </Text>
              <Text size="xs" className={colorScheme === "dark" ? "text-white/60" : "text-gray-500"}>
                Actual user actions tracked
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card
              shadow="sm"
              radius="lg"
              p="lg"
              style={{
                background: colorScheme === "dark" ? "rgba(59, 130, 246, 0.1)" : "rgba(59, 130, 246, 0.05)",
                border: "1px solid rgba(59, 130, 246, 0.2)",
              }}
            >
              <Group gap="sm" mb="xs">
                <IconEye size={20} className="text-blue-500" />
                <Text fw={600} size="sm" className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
                  Displayed Actions
                </Text>
              </Group>
              <Text size="2rem" fw={700} className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
                {staticMetrics ? MetricsService.formatNumber(staticMetrics.totalActions) : "Hidden"}
              </Text>
              <Text size="xs" className={colorScheme === "dark" ? "text-white/60" : "text-gray-500"}>
                {staticMetrics ? "Shown to public users" : "Below threshold - not displayed"}
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card
              shadow="sm"
              radius="lg"
              p="lg"
              style={{
                background: colorScheme === "dark" ? "rgba(139, 92, 246, 0.1)" : "rgba(139, 92, 246, 0.05)",
                border: "1px solid rgba(139, 92, 246, 0.2)",
              }}
            >
              <Group gap="sm" mb="xs">
                <IconTrendingUp size={20} className="text-purple-500" />
                <Text fw={600} size="sm" className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
                  Weekly Growth
                </Text>
              </Group>
              <Text size="2rem" fw={700} className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
                {realMetrics.weeklyGrowth.toFixed(1)}%
              </Text>
              <Text size="xs" className={colorScheme === "dark" ? "text-white/60" : "text-gray-500"}>
                Real growth rate
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card
              shadow="sm"
              radius="lg"
              p="lg"
              style={{
                background: colorScheme === "dark" ? "rgba(249, 115, 22, 0.1)" : "rgba(249, 115, 22, 0.05)",
                border: "1px solid rgba(249, 115, 22, 0.2)",
              }}
            >
              <Group gap="sm" mb="xs">
                <IconUsers size={20} className="text-orange-500" />
                <Text fw={600} size="sm" className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
                  Active States
                </Text>
              </Group>
              <Text size="2rem" fw={700} className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
                {Object.keys(realMetrics.actionsByState).length}
              </Text>
              <Text size="xs" className={colorScheme === "dark" ? "text-white/60" : "text-gray-500"}>
                States with actions
              </Text>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Comparison Table */}
        <Card
          shadow="sm"
          radius="lg"
          p="lg"
          style={{
            background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(255, 255, 255, 0.7)",
            border: colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
          }}
        >
          <Title order={3} mb="md" className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
            Real vs. Displayed Metrics
          </Title>

          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Metric</Table.Th>
                <Table.Th>Displayed to Public</Table.Th>
                <Table.Th>Real Data</Table.Th>
                <Table.Th>Difference</Table.Th>
                <Table.Th>Accuracy</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {comparisonData.map((row) => {
                const difference = row.static - row.real
                const accuracy = row.real > 0 ? (row.real / row.static) * 100 : 0

                return (
                  <Table.Tr key={row.metric}>
                    <Table.Td fw={500}>{row.metric}</Table.Td>
                    <Table.Td>{MetricsService.formatNumber(row.static)}</Table.Td>
                    <Table.Td>{MetricsService.formatNumber(row.real)}</Table.Td>
                    <Table.Td>
                      <Text color={difference > 0 ? "red" : "green"} fw={500}>
                        {difference > 0 ? "+" : ""}
                        {MetricsService.formatNumber(difference)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Progress
                        value={Math.min(accuracy, 100)}
                        color={accuracy > 80 ? "green" : accuracy > 50 ? "yellow" : "red"}
                        size="sm"
                      />
                      <Text size="xs" mt={4}>
                        {accuracy.toFixed(1)}%
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Card>

        {/* Daily Actions Chart */}
        <Card
          shadow="sm"
          radius="lg"
          p="lg"
          style={{
            background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(255, 255, 255, 0.7)",
            border: colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
          }}
        >
          <Title order={3} mb="md" className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
            Daily Action Breakdown
          </Title>

          <Grid gutter="md">
            {Object.entries(realMetrics.dailyActions)
              .sort(([a], [b]) => b.localeCompare(a))
              .slice(0, 7)
              .map(([date, count]) => (
                <Grid.Col key={date} span={{ base: 12, sm: 6, md: 4 }}>
                  <Group justify="space-between">
                    <Text size="sm" className={colorScheme === "dark" ? "text-white/80" : "text-gray-700"}>
                      {new Date(date).toLocaleDateString()}
                    </Text>
                    <Badge variant="light" color="blue" size="sm">
                      {count} actions
                    </Badge>
                  </Group>
                </Grid.Col>
              ))}
          </Grid>
        </Card>

        {/* State Breakdown */}
        <Card
          shadow="sm"
          radius="lg"
          p="lg"
          style={{
            background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(255, 255, 255, 0.7)",
            border: colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
          }}
        >
          <Title order={3} mb="md" className={colorScheme === "dark" ? "text-white" : "text-gray-900"}>
            Actions by State (Real Data)
          </Title>

          <Grid gutter="sm">
            {Object.entries(realMetrics.actionsByState)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 10)
              .map(([state, count]) => (
                <Grid.Col key={state} span={{ base: 12, sm: 6, md: 4 }}>
                  <Group justify="space-between">
                    <Text size="sm" className={colorScheme === "dark" ? "text-white/80" : "text-gray-700"}>
                      {state || "Unknown"}
                    </Text>
                    <Badge variant="light" color="green" size="sm">
                      {count} actions
                    </Badge>
                  </Group>
                </Grid.Col>
              ))}
          </Grid>
        </Card>

        {/* Footer Info */}
        <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
          <Text size="sm">
            <strong>Note:</strong> This dashboard shows real tracking data vs. what is displayed to public users.
            {staticMetrics
              ? " Real metrics are now being shown to the public since we've reached the minimum threshold."
              : " Public metrics are hidden until we reach 100 total actions."}
          </Text>
        </Alert>
      </Stack>
    </Container>
  )
}

export const Route = createFileRoute("/metrics")({
  beforeLoad: ({ search }) => {
    const searchParams = new URLSearchParams(search as string)
    const key = searchParams.get("key")

    if (key !== "rtefn-internal-2025") {
      throw redirect({ to: "/" })
    }
  },
  component: MetricsDashboard,
})
